<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcContractCard',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    item: {
      type: Object,
      required: true,
    },
    displayRental: {
      type: Boolean,
      required: false,
      default: true,
    },
    displayCounterparty: {
      type: Boolean,
      required: false,
      default: true,
    },
    size: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const ux = ref([]);

  const $data = computed(() => ({
    ux: ux.value,
  }));

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [{ name: 'ux' }],
    loaders: {},
    ux: {},
    dataRefs: { ux },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.item,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('item');
    }
  );
  watch(
    () => props.displayRental,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('displayRental');
    }
  );
  watch(
    () => props.displayCounterparty,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('displayCounterparty');
    }
  );
  watch(
    () => props.size,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('size');
    }
  );

  const act_7315092382398562_reloadComponentData_sjk4enrga =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_vd7zmx6iq =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_sjk4enrga,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_vd7zmx6iq],
    event: 'reload',
  });

  const actions = {
    inst_evh_7315092382398562_reload_vd7zmx6iq,
    inst_evh_7315092382398562_reload,
  };

  const evh_7315092382398562_reload_vd7zmx6iq = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_vd7zmx6iq.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <ItpBox alignItems="center" class="pb-3">
      <ItpIcon
        :name="$config.contract.icon"
        spacing="me-3"
        :title="pgettext('Contract', 'Contract')"
        class="me-2"
      >
      </ItpIcon>
      <ItpLink
        :to="{
          name: 'app.contract',
          params: { id: item.id },
        }"
      >
        <ItpText strong :text="item.contract_number"> </ItpText>
      </ItpLink>
      <UcContractTypeBadge :value="item.contract_type" class="mx-3">
      </UcContractTypeBadge>
      <UcContractStatusBadge :value="item.status" class="me-3">
      </UcContractStatusBadge>
      <UcValidityPeriod
        :from="item.valid_from"
        :to="item.valid_to"
        class="pe-3"
      >
      </UcValidityPeriod>
    </ItpBox>
    <ItpBox alignItems="center">
      <UcCompanyMiniCard
        role="partner"
        :item="{ id: item.counterparty_id, name: item.counterparty_name }"
        class="pe-3"
        v-if="displayCounterparty"
      >
      </UcCompanyMiniCard>
      <UcRentalMiniCard
        :item="{ id: item.rental_id, name: item.rental_name }"
        class="pe-3"
        v-if="displayRental"
      >
      </UcRentalMiniCard>
    </ItpBox>
  </ItpUsercontrol>
</template>
